import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { clientApi, interactionApi } from '../../services/api';
import type { Client, ClientInteraction } from '../../types';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  UserIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [interactions, setInteractions] = useState<ClientInteraction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadClientData(id);
    }
  }, [id]);

  const loadClientData = async (clientId: string) => {
    try {
      setLoading(true);
      const [clientData, interactionsData] = await Promise.all([
        clientApi.getClient(clientId),
        interactionApi.getInteractions({ client: clientId, ordering: '-interaction_date' })
      ]);
      
      setClient(clientData);
      setInteractions(interactionsData.results);
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!client || !id) return;
    
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await clientApi.deleteClient(id);
        navigate('/clients');
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  const getPersonTypeLabel = (type: string) => {
    return type === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica';
  };

  const getInteractionTypeIcon = (type: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      call: PhoneIcon,
      email: EnvelopeIcon,
      whatsapp: ChatBubbleLeftRightIcon,
      visit: MapPinIcon,
      meeting: BuildingOfficeIcon,
      other: ClockIcon
    };
    
    const Icon = icons[type] || ClockIcon;
    return <Icon className="h-4 w-4" />;
  };

  const getInteractionTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      call: 'Ligação',
      email: 'Email',
      whatsapp: 'WhatsApp',
      visit: 'Visita',
      meeting: 'Reunião',
      other: 'Outro'
    };
    
    return labels[type] || 'Outro';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-8">
        <p className="text-secondary-600">Cliente não encontrado</p>
        <Link to="/clients" className="btn-primary mt-4">
          Voltar para Clientes
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-base font-bold text-secondary-900">{client.name}</h1>
          <p className="text-xs text-secondary-600">
            {getPersonTypeLabel(client.person_type)} • {client.formatted_document || client.cpf_cnpj}
          </p>
        </div>
        <div className="flex space-x-2">
          <Link
            to={`/clients/${client.id}/edit`}
            className="btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-1.5" />
            Editar
          </Link>
          <button
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            <TrashIcon className="h-4 w-4 mr-1.5" />
            Excluir
          </button>
        </div>
      </div>

      {/* Status Badge */}
      <div>
        {client.is_active ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
            Ativo
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
            Inativo
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-1">
          {/* Contact Information */}
          <div className="card">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">
              Informações de Contato
            </h3>
            
            <div className="space-y-1">
              <div className="flex items-center space-x-1">
                <EnvelopeIcon className="h-4 w-4 text-secondary-400" />
                <span className="text-secondary-900">{client.email}</span>
              </div>
              
              <div className="flex items-center space-x-1">
                <PhoneIcon className="h-4 w-4 text-secondary-400" />
                <span className="text-secondary-900">{client.phone}</span>
              </div>
              
              {client.whatsapp && (
                <div className="flex items-center space-x-1">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-secondary-400" />
                  <span className="text-secondary-900">{client.whatsapp}</span>
                  <span className="text-secondary-500">(WhatsApp)</span>
                </div>
              )}
              
              {client.website && (
                <div className="flex items-center space-x-1">
                  <BuildingOfficeIcon className="h-4 w-4 text-secondary-400" />
                  <a 
                    href={client.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800"
                  >
                    {client.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Address Information */}
          {client.full_address && (
            <div className="card">
              <h3 className="text-xs font-semibold text-secondary-900 mb-1">
                Endereço
              </h3>
              
              <div className="flex items-start space-x-1">
                <MapPinIcon className="h-4 w-4 text-secondary-400 mt-1" />
                <span className="text-secondary-900">{client.full_address}</span>
              </div>
            </div>
          )}

          {/* Signatory Information */}
          {client.signatory_name && (
            <div className="card">
              <h3 className="text-xs font-semibold text-secondary-900 mb-1">
                Dados do Signatário
              </h3>
              
              <div className="space-y-1">
                <div className="flex items-center space-x-1">
                  <UserIcon className="h-4 w-4 text-secondary-400" />
                  <span className="text-secondary-900">{client.signatory_name}</span>
                  {client.signatory_role && (
                    <span className="text-secondary-500">({client.signatory_role})</span>
                  )}
                </div>
                
                {client.signatory_email && (
                  <div className="flex items-center space-x-1">
                    <EnvelopeIcon className="h-4 w-4 text-secondary-400" />
                    <span className="text-secondary-900">{client.signatory_email}</span>
                  </div>
                )}
                
                {client.signatory_document && (
                  <div className="flex items-center space-x-1">
                    <BuildingOfficeIcon className="h-4 w-4 text-secondary-400" />
                    <span className="text-secondary-900">{client.signatory_document}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Observations */}
          {client.observations && (
            <div className="card">
              <h3 className="text-xs font-semibold text-secondary-900 mb-1">
                Observações
              </h3>
              <p className="text-secondary-700 whitespace-pre-wrap">
                {client.observations}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-1">
          {/* Quick Actions */}
          <div className="card">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">
              Ações Rápidas
            </h3>
            
            <div className="space-y-1">
              <button className="w-full text-left p-1 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
                <div className="font-medium text-xs text-secondary-900">Nova Interação</div>
                <div className="text-xs text-secondary-600">Registrar contato com cliente</div>
              </button>
              
              <button className="w-full text-left p-1 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
                <div className="font-medium text-xs text-secondary-900">Nova Proposta</div>
                <div className="text-xs text-secondary-600">Criar proposta comercial</div>
              </button>
              
              <button className="w-full text-left p-1 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
                <div className="font-medium text-xs text-secondary-900">Upload Documento</div>
                <div className="text-xs text-secondary-600">Anexar arquivo</div>
              </button>
            </div>
          </div>

          {/* Recent Interactions */}
          <div className="card">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-semibold text-secondary-900">
                Interações Recentes
              </h3>
              <button className="text-primary-600 hover:text-primary-800 text-xs">
                <PlusIcon className="h-4 w-4 inline mr-1" />
                Nova
              </button>
            </div>
            
            {interactions.length === 0 ? (
              <p className="text-secondary-500 text-xs">Nenhuma interação registrada</p>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {interactions.slice(0, 5).map((interaction) => (
                  <div
                    key={interaction.id}
                    className="border border-secondary-200 rounded-lg p-1"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      {getInteractionTypeIcon(interaction.interaction_type)}
                      <span className="font-medium text-xs text-secondary-900">
                        {getInteractionTypeLabel(interaction.interaction_type)}
                      </span>
                      <span className="text-xs text-secondary-500">
                        {new Date(interaction.interaction_date).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    
                    <div className="text-xs text-secondary-900 mb-1">
                      {interaction.subject}
                    </div>
                    
                    <div className="text-xs text-secondary-600 line-clamp-1">
                      {interaction.description}
                    </div>
                    
                    {interaction.responsible_name && (
                      <div className="text-xs text-secondary-500 mt-1">
                        Por: {interaction.responsible_name}
                      </div>
                    )}
                  </div>
                ))}
                
                {interactions.length > 5 && (
                  <div className="text-center pt-2">
                    <button className="text-primary-600 hover:text-primary-800 text-xs">
                      Ver todas as interações
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Client Statistics */}
          <div className="card">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">
              Estatísticas
            </h3>
            
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-xs text-secondary-600">Total de Interações:</span>
                <span className="font-medium text-xs">{interactions.length}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-xs text-secondary-600">Cliente desde:</span>
                <span className="font-medium text-xs">
                  {new Date(client.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-xs text-secondary-600">Última atualização:</span>
                <span className="font-medium text-xs">
                  {new Date(client.updated_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;